// Industry Network transmission — dramatic interactive invitation for brands & sponsors.
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Kicker } from "@/components/Section";
import { windowState } from "@/lib/windows";
import { TransmissionIntro } from "@/components/transmission/TransmissionIntro";
import { ClearanceScan } from "@/components/transmission/ClearanceScan";
import { ArchitectNote } from "@/components/transmission/ArchitectNote";
import { ContributionGrid } from "@/components/transmission/ContributionGrid";
import { MissionBuilder } from "@/components/transmission/MissionBuilder";
import { MissionCards } from "@/components/transmission/MissionCards";
import { PartnerWall } from "@/components/transmission/PartnerWall";

const INTRO_LINES = [
  { kicker: "Scanning frequencies", text: "Unknown Signal Detected" },
  { kicker: "Trace complete", text: "Source Identified: Big Little Village" },
  { kicker: "Transmission 02", text: "A Message from The Architect" },
];

const WE_HAVE = [
  "We have photographers.",
  "We have filmmakers.",
  "We have designers.",
  "We have stylists.",
  "We have beauty professionals.",
  "We have storytellers.",
  "We have emerging talent waiting for something real to build.",
];

const RESOURCES = ["Products", "Spaces", "Equipment", "Campaign briefs", "Experiences", "Knowledge", "Resources", "Opportunities"];

export default function TransmissionIndustry() {
  const reduced = useReducedMotion();
  const [introDone, setIntroDone] = useState(false);
  const [cleared, setCleared] = useState(() => {
    try { return !!sessionStorage.getItem("blv_clearance_granted"); } catch { return false; }
  });
  const brandOpen = windowState("brand").status === "open";

  return (
    <div className="bg-onyx text-cream">
      <TransmissionIntro lines={INTRO_LINES} onDone={() => setIntroDone(true)} testId="industry-intro" />
      {introDone && !cleared && <ClearanceScan onDone={() => setCleared(true)} />}
      {introDone && cleared && (
        <div data-testid="transmission-industry-page">
          {/* Message from The Architect */}
          <section className="grain-dark relative overflow-hidden border-b border-gold/20 bg-onyx py-24 sm:py-32">
            <div className="vignette pointer-events-none absolute inset-0" />
            <div className="drift-slow pointer-events-none absolute left-1/2 top-1/3 h-[70vmin] w-[70vmin] -translate-x-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.09) 0%, transparent 60%)" }} />
            <div className="relative mx-auto max-w-[860px] px-4 text-center sm:px-6">
              <div className="space-y-4">
                {WE_HAVE.map((l, i) => (
                  <motion.p key={l}
                    initial={{ opacity: 0, y: reduced ? 0 : 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55, delay: reduced ? 0 : i * 0.1 }}
                    className={`font-cinzel uppercase tracking-[0.15em] ${i === WE_HAVE.length - 1 ? "text-gold text-lg sm:text-2xl" : "text-cream/85 text-base sm:text-xl"}`}>
                    {l}
                  </motion.p>
                ))}
              </div>
              <Reveal delay={0.25}>
                <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.4em] text-silver/60">What we need now is a mission.</p>
                <div className="gold-rule mx-auto mt-8 w-56" />
                <h1 className="mt-10 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-6xl" data-testid="industry-headline">
                  Your brand could be<br /><span className="text-gold-metallic">the next mission.</span>
                </h1>
                <p className="mx-auto mt-6 max-w-xl font-body text-lg leading-relaxed text-silver/85">
                  Instead of simply sponsoring a program, give emerging creatives something real to create around.
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-2">
                  {RESOURCES.map((r) => (
                    <span key={r} className="border border-gold/35 bg-charcoal px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-goldlight">{r}</span>
                  ))}
                </div>
              </Reveal>
            </div>
          </section>

          {/* Select your contribution */}
          <section className="grain-dark border-b border-gold/20 bg-charcoal py-20 sm:py-28" id="contribute">
            <div className="mx-auto max-w-[1000px] px-4 sm:px-6">
              <Reveal>
                <Kicker>Choose your mission</Kicker>
                <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">Select your contribution</h2>
                <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-silver/80">Tap a file to see what it unlocks — and which creative units it deploys.</p>
              </Reveal>
              <div className="mt-10"><ContributionGrid /></div>
            </div>
          </section>

          {/* Architect note */}
          <section className="grain-dark border-b border-gold/20 bg-onyx py-16 text-center sm:py-20">
            <div className="mx-auto max-w-[860px] px-4 sm:px-6">
              <ArchitectNote quote="Give us the resource. We'll build the mission." />
            </div>
          </section>

          {/* Build your mission */}
          <section className="grain-dark border-b border-gold/20 bg-charcoal py-20 sm:py-28" id="build">
            <div className="mx-auto max-w-[860px] px-4 sm:px-6">
              <Reveal>
                <Kicker>Preliminary parameters</Kicker>
                <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">Build your mission</h2>
                <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-silver/80">Three selections. No commitment. Your parameters attach to your partnership file automatically.</p>
              </Reveal>
              <div className="mt-10"><MissionBuilder /></div>
            </div>
          </section>

          {/* Sample missions */}
          <section className="grain-dark relative border-b border-gold/20 bg-onyx py-20 sm:py-28">
            <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.03]" />
            <div className="relative mx-auto max-w-[1100px] px-4 sm:px-6">
              <Reveal>
                <Kicker>Live mission files</Kicker>
                <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">Missions awaiting partners</h2>
                <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-silver/80">Sample operations showing how a product, a room, or a camera becomes someone's first real campaign.</p>
              </Reveal>
              <div className="mt-10"><MissionCards ctaTo="/apply/brand" /></div>
            </div>
          </section>

          {/* Partner mission wall */}
          <section className="grain-dark border-b border-gold/20 bg-charcoal py-20 sm:py-28">
            <div className="mx-auto max-w-[1100px] px-4 sm:px-6">
              <Reveal>
                <Kicker>The Mission Wall</Kicker>
                <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">Partners already in the operation</h2>
              </Reveal>
              <div className="mt-10"><PartnerWall showCta={false} /></div>
            </div>
          </section>

          {/* Final clearance */}
          <section className="grain-dark relative bg-charcoal py-20 sm:py-28">
            <div className="relative mx-auto max-w-[720px] px-4 text-center sm:px-6">
              <Reveal>
                <div className="gold-frame grain-dark relative bg-onyx p-8 sm:p-12">
                  <div className="pointer-events-none absolute inset-2 border border-gold/15" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.45em] text-gold" data-testid="industry-clearance-ready">Clearance request ready</p>
                  <h2 className="mt-5 font-display text-3xl uppercase leading-[0.95] tracking-tight text-metallic sm:text-5xl">Report to The Architect.</h2>
                  <p className="mx-auto mt-5 max-w-md font-body text-base leading-relaxed text-silver/85">
                    Your organization may have exactly what one of our emerging creatives needs for their next mission.
                  </p>
                  <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <Link to="/apply/brand" data-testid="submit-partnership-file-final-btn"
                      className="btn-sweep inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
                      Submit Partnership File <ArrowRight size={15} />
                    </Link>
                    <Link to="/" className="border border-silver/40 px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">
                      Return to the Village
                    </Link>
                  </div>
                  {!brandOpen && (
                    <p className="mt-4 inline-block border border-gold/40 bg-charcoal px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-goldlight">
                      Network window opens {windowState("brand").opensLabel} — review the file now, transmit on opening day
                    </p>
                  )}
                </div>
              </Reveal>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
